import { Component } from "./components/Component"
import { CustomComponent } from "./components/CustomComponent"
import { DrawZIndex } from "./drawutils"
import { ArrayFillUsing, isString } from "./utils"

export type ComponentListChangeReason = "structure" | "metadata" | "value"
type ComponentListChangeListener = (reason: ComponentListChangeReason) => void

export class ComponentList {

    private _componentsByZIndex = ArrayFillUsing(() => [] as Component[], 3)
    private _componentsById = new Map<string, Component>()
    private readonly _changeListeners = new Set<ComponentListChangeListener>()

    public *all() {
        for (const compList of this._componentsByZIndex) {
            for (const comp of compList) {
                yield comp
            }
        }
    }

    public *allInReversedZIndexOrder() {
        for (let i = this._componentsByZIndex.length - 1; i >= 0; i--) {
            const compList = this._componentsByZIndex[i]
            for (const comp of compList) {
                yield comp
            }
        }
    }

    public *withZIndex(zIndex: DrawZIndex) {
        for (const comp of this._componentsByZIndex[zIndex]) {
            yield comp
        }
    }

    public add(comp: Component) {
        const z = comp.drawZIndex
        this._componentsByZIndex[z].push(comp)

        let id = comp.ref
        let oldComp
        if (id === undefined) {
            id = this.generateIdFor(comp)
            comp.doSetValidatedId(id)
        } else if ((oldComp = this._componentsById.get(id)) !== undefined) {
            console.error(`Component with id '${id}' already exists and will be renamed`)
            const idForOldComp = this.generateIdFor(oldComp)
            oldComp.doSetValidatedId(idForOldComp)
            this._componentsById.set(idForOldComp, oldComp)
        }
        this._componentsById.set(id, comp)
        this.notifyChangeListeners()
    }

    public addChangeListener(listener: ComponentListChangeListener): () => void {
        this._changeListeners.add(listener)
        return () => this._changeListeners.delete(listener)
    }

    public notifyChangeListeners(reason: ComponentListChangeReason = "structure") {
        for (const listener of this._changeListeners) {
            listener(reason)
        }
    }

    public contains(type: string): boolean {
        for (const comp of this.all()) {
            if (comp.def.type === type) {
                return true
            }
        }
        return false
    }

    public updateCustomComponents(type: string) {
        for (const comp of [...this.all()]) {
            if (comp.def.type === type && comp instanceof CustomComponent) {
                comp.updateFromDef()
            }
        }
    }

    public changeIdOf(comp: Component, newId: string) {
        // We must make sure that this new id is not already used before calling
        const oldId = comp.ref
        if (oldId !== undefined) {
            const deleted = this._componentsById.delete(oldId)
            if (!deleted) {
                console.warn(`Component with id '${oldId}' not found`)
            }
        }
        if (this._componentsById.has(newId)) {
            throw new Error(`Component with id '${newId}' already exists`)
        }
        comp.doSetValidatedId(newId)
        this._componentsById.set(newId, comp)
    }

    public regenerateIdOf(comp: Component) {
        const newId = this.generateIdFor(comp)
        this.changeIdOf(comp, newId)
    }

    public swapIdsOf(comp1: Component, comp2: Component) {
        const id1 = comp1.ref
        const id2 = comp2.ref
        if (id1 === undefined || id2 === undefined) {
            throw new Error("Cannot swap ids of components without ids")
        }
        comp1.doSetValidatedId(id2)
        comp2.doSetValidatedId(id1)
        this._componentsById.set(id1, comp2)
        this._componentsById.set(id2, comp1)
    }

    public get(id: string): Component | undefined {
        return this._componentsById.get(id)
    }

    public looksLikeAutoGeneratedId(comp: Component): boolean {
        const ref = comp.ref
        if (ref === undefined) {
            return false
        }
        const prefix = ComponentList.idPrefixFor(comp)
        if (!ref.startsWith(prefix)) {
            return false
        }
        const suffix = ref.slice(prefix.length)
        if (isNaN(Number(suffix))) {
            return false
        }
        return true
    }

    private generateIdFor(comp: Component): string {
        const prefix = ComponentList.idPrefixFor(comp)
        let i = 0
        let id
        while (this._componentsById.has(id = `${prefix}${i}`)) {
            i++
        }
        return id
    }

    private static idPrefixFor(comp: Component) {
        const prefixFromDef = comp.def.idPrefix
        return isString(prefixFromDef) ? prefixFromDef : prefixFromDef(comp)
    }

    public tryDelete(comp: Component): boolean {
        return this.tryDeleteWhere(c => c === comp, true).length > 0
    }

    public tryDeleteWhere(cond: (e: Component) => boolean, onlyOne: boolean): Component[] {
        const deletedComps: Component[] = []

        // delete components
        outer:
        for (const compList of this._componentsByZIndex) {
            for (let i = 0; i < compList.length; i++) {
                const comp = compList[i]
                if (cond(comp)) {
                    comp.destroy()
                    compList.splice(i, 1)
                    deletedComps.push(comp)
                    if (onlyOne) {
                        break outer
                    }
                }
            }
        }

        // delete ids
        for (const comp of deletedComps) {
            const id = comp.ref
            if (id === undefined) {
                console.warn("Removing component without id")
            } else if (!this._componentsById.has(id)) {
                console.warn(`Removing component with id '${id}' but no component with that id exists`)
            } else {
                this._componentsById.delete(id)
            }
        }

        // remove anchors
        for (const comp of deletedComps) {
            for (const anchored of comp.anchoredDrawables) {
                anchored.anchor = undefined
            }
        }

        if (deletedComps.length > 0) {
            this.notifyChangeListeners()
        }

        return deletedComps
    }

    public clearAll() {
        const hadComponents = this._componentsById.size > 0
        for (const compList of this._componentsByZIndex) {
            compList.length = 0
        }
        this._componentsById.clear()
        if (hadComponents) {
            this.notifyChangeListeners()
        }
    }

    public boundingRect(): DOMRectReadOnly {
        let left = Infinity
        let right = -Infinity
        let top = Infinity
        let bottom = -Infinity
        for (const comp of this.all()) {
            const compWidth = comp.width
            const compHeight = comp.height
            if (compWidth === 0 || compHeight === 0) {
                continue
            }
            const compLeft = comp.posX - compWidth / 2
            const compRight = comp.posX + compWidth / 2
            const compTop = comp.posY - compHeight / 2
            const compBottom = comp.posY + compHeight / 2
            if (compLeft < left) {
                left = compLeft
            }
            if (compRight > right) {
                right = compRight
            }
            if (compTop < top) {
                top = compTop
            }
            if (compBottom > bottom) {
                bottom = compBottom
            }
        }
        if (left === Infinity) {
            left = 0
        }
        if (right === -Infinity) {
            right = 0
        }
        if (top === Infinity) {
            top = 0
        }
        if (bottom === -Infinity) {
            bottom = 0
        }
        return new DOMRectReadOnly(left, top, right - left, bottom - top)
    }
}
